<template>
  <div class="flex flex-row flex-wrap w-10 mt-2 mr-2">
    <HeadTitle
      title="Detalle de flujo de aprobación"
      description="En esta sección podras editar, el alcance de los pasos de aprobación de las solicitudes de licencias y vacaciones"
    />
    <EditRecordButton @update:is-edit-mode="toggleEditMode" />
  </div>
  <br />
  <div v-if="isLoading" class="grid w-10 mt-4">
    <div class="col-12">
      <Skeleton width="10rem" height="1.5rem" class="mb-3"></Skeleton>
      <div class="flex flex-column md:col-4">
        <Skeleton width="100%" height="2.5rem" class="mb-2"></Skeleton>
      </div>
      <div class="flex flex-column md:col-4 mt-3">
        <Skeleton width="100%" height="2.5rem" class="mb-2"></Skeleton>
      </div>
    </div>
    <div class="col-12">
      <Divider />
    </div>
    <div class="col-12">
      <Skeleton width="10rem" height="1.5rem" class="mb-3"></Skeleton>
      <Skeleton width="100%" height="400px"></Skeleton>
    </div>
    <div class="col-12">
      <Divider />
    </div>
    <div class="col-12">
      <Skeleton width="10rem" height="1.5rem" class="mb-3"></Skeleton>
      <Skeleton width="100%" height="5rem"></Skeleton>
    </div>
  </div>
  <div v-else>
    <Step :step-number="1" title="Alcance del flujo de aprobación">
      <div class="flex flex-column">
        <Dropdown
          v-model="selectedEntity"
          option-label="name"
          :options="entities"
          class="w-8 md:w-14rem"
          :disabled="!isEditMode"
        ></Dropdown>
        <div
          v-if="selectedEntity.code === 'E'"
          class="flex flex-column md:w-full mt-3"
        >
          <MultiSelectDialog
            header-title="Seleccionar empleados"
            label="Seleccionar empleados"
            :options="employees"
            @update:visible="updateDialogVisibility"
            :disabled="!isEditMode"
            show-key="name"
            value-key="id"
            :required="true"
            v-model="selectedEmployees"
          />
          <small id="text-error" class="p-error">{{
            employeesError || '&nbsp;'
          }}</small>
        </div>
        <div
          v-if="selectedEntity.code === 'N'"
          class="flex flex-column md:w-full mt-3"
        >
          <MultiSelectDialog
            header-title="Seleccionar nóminas"
            label="Seleccionar nóminas"
            @update:visible="updateDialogVisibility"
            :options="payrolls"
            v-model="selectedPayrolls"
            :disabled="!isEditMode"
            show-key="name"
            value-key="id"
            :required="true"
          />
          <small id="text-error" class="p-error">{{
            payrollsError || '&nbsp;'
          }}</small>
        </div>
      </div>
    </Step>
    <Divider />
    <Step :step-number="2" title="Pasos de aprobación">
      <DataTable
        v-model:editingRows="editingRows"
        :value="approvalSteps.data"
        editMode="row"
        dataKey="id"
        @row-edit-save="onRowEditSave"
        tableClass="editable-cells-table"
        tableStyle="min-width: 30rem"
        scrollHeight="600px"
        scrollable
        class="tabla"
      >
        <Column header="#" style="width: 15%" filterField="supervisor">
          <template #body="{ data, index }">
            <Chip
              class="flex justify-content-center w-5 h-2rem"
              :class="{ 'bg-teal-200': data.approver?.name !== '' }"
              :label="String(index + 1)"
            />
          </template>
        </Column>
        <Column field="approver" header="Aprobador" style="width: 30%">
          <template #body="{ data, field }">
            <span v-if="!data[field] && data.useEmployeeSupervisor">
              Supervisor del empleado
            </span>
            <span v-else-if="data[field]?.name">
              {{ data[field].name }}
            </span>
            <span v-else>
              <label class="text-600 font-medium">Sin asignar</label>
            </span>
          </template>
          <template #editor="{ data, field }">
            <Dropdown
              v-model="data[field]"
              :options="approvers"
              optionLabel="name"
              :placeholder="calcApproverPlaceHolder(data)"
              filter
            >
              <template #option="slotProps">
                {{ slotProps.option.name }}
              </template>
            </Dropdown>
          </template>
        </Column>
        <Column field="isBackup" header="Backup" style="width: 20%">
          <template #editor="{ data, field }">
            <Checkbox v-model="data[field]" :binary="true" />
          </template>
          <template #body="{ data, field }">
            {{ data[field] ? 'Si' : 'No' }}
          </template>
        </Column>
        <Column field="weight" header="Prioridad" style="max-width: 5%">
          <template #body="{ data, field }">
            <span class="text-green-800">
              {{ data[field] }}
            </span>
          </template>
          <template #editor="{ data, field }">
            <InputNumber
              class="w-2"
              v-model="data[field]"
              inputId="minmax"
              :min="0"
              :max="100"
              :disabled="data.isBackup"
            />
          </template>
        </Column>
        <Column field="description" header="Descripción">
          <template #body="{ data, field }">
            {{ data[field] }}
          </template>
          <template #editor="{ data, field }">
            <InputText v-model="data[field]"></InputText>
          </template>
        </Column>
        <Column
          :rowEditor="true"
          style="width: 10%; min-width: 8rem"
          bodyStyle="text-align:center"
          v-if="isEditMode"
        >
        </Column>
        <Column v-if="isEditMode">
          <template #body="{ data, index }">
            <div class="actions">
              <Button
                class="action-btn view p-button-sm"
                icon="pi pi-trash"
                @click="
                  onRowDelete(
                    $event,
                    data['id'],
                    data.approver?.id,
                    data.approver?.name
                  )
                "
                severity="danger"
                v-tooltip.bottom="'Eliminar'"
                :disabled="approvalSteps.data.length === index + 1"
              />
            </div>
          </template>
        </Column>
      </DataTable>
    </Step>
    <Divider></Divider>
    <Step :step-number="3" title="Agregar descripción">
      <div class="flex flex-column md:w-5 h-5">
        <Textarea
          class="mt-2"
          style="min-width: 250px"
          v-model="description"
          :disabled="!isEditMode"
        />
        <small id="text-error" class="p-error">{{
          descriptionError || '&nbsp;'
        }}</small>
      </div>
    </Step>
  </div>
  <Footer>
    <template #left>
      <AuditButton
        :record-ids="recordsIds"
        :root-id="approvalFlowId"
        :entities="['ApprovalFlow', 'ApprovalFlowStep']"
      />
    </template>
    <Button
      label="Cancelar"
      class="mr-2 text-sm font-light"
      severity="secondary"
      @click="
        router.push('/payrollsConfig/' + payrollConfigId + '/approvalFlows')
      "
    />
    <Button
      label="Guardar"
      class="font-normal text-sm font-light"
      severity="primary"
      @click="onSave"
      v-show="isEditMode"
      :disabled="!isValidToSave"
      :loading="saving"
    />
  </Footer>
</template>
<script setup lang="ts">
  import { computed, onMounted, reactive, ref, watch } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import Footer from '@/shared/components/Footer.vue'
  import Step from '@/shared/components/Step.vue'
  import * as yup from 'yup'
  import MultiSelectDialog from '@/shared/components/MultiSelectDialog.vue'
  import { getApproversByCompanyId } from '../services/ApproverService'
  import { getPayrollsByConfigId } from '@/modules/payrolls-config/services/PayrollService'
  import { getEmplopeesByPayrollConfigId } from '@/modules/employees/services/EmployeeService'
  import ApprovalStep from '../models/ApprovalStep'
  import { storeToRefs } from 'pinia'
  import Checkbox from 'primevue/checkbox'
  import useCompany from '@/store/company'
  import { useField } from 'vee-validate'
  import useNotifications from '@/store/notification'
  import {
    getApprovalFlowById,
    updateApprovalFlow,
  } from '../services/ApprovalFlowService'
  import {
    createApprovalFlowSteps,
    deleteApprovalFlowSteps,
    getApprovalFlowSteps,
    updateApprovalFlowSteps,
  } from '../services/ApprovalFlowStepService'
  import {
    CreateApprovalFlowStepDto,
    ApprovalStepInputDto,
  } from '../services/interfaces/CreateApprovalFlowStepDto'
  import {
    UpdateApprovalFlowStepDto,
    UpdateApprovalStepInputDto,
  } from '../services/interfaces/UpdateApprovalFlowStepInputDto'
  import DeleteApprovalFlowStepInputDto from '../services/interfaces/DeleteApprovalFlowStepInputDto'
  import AuditButton from '@/shared/components/AuditButton.vue'
  import HeadTitle from '@/shared/components/HeadTitle.vue'
  import EditRecordButton from '@/shared/components/EditRecordButton.vue'

  const path = useRoute()
  const router = useRouter()
  const payrollConfigId = ref()
  const approvalFlowId = ref()
  const isEditMode = ref<boolean>(false)
  const isLoading = ref(false)
  const saving = ref(false)

  const recordsIds = computed(() => {
    return [approvalFlowId.value, ...approvalSteps.data.map((step) => step.id)]
  })

  //save the steps ids from the steps that would be deleted
  const stepsToDelete = reactive<DeleteApprovalFlowStepInputDto>({
    stepsIds: [] as string[],
  })

  const stepsToUpdate = reactive<UpdateApprovalFlowStepDto>({
    flowSteps: [] as UpdateApprovalStepInputDto[],
  })

  const stepsToCreate = reactive<CreateApprovalFlowStepDto>({
    steps: [] as ApprovalStepInputDto[],
  })

  const entities = ref([
    { code: 'ALL', name: 'Todas las nóminas' },
    { code: 'E', name: 'Empleados' },
    { code: 'N', name: 'Nóminas' },
  ])

  const companyStore = useCompany()
  const { company } = storeToRefs(companyStore)

  companyStore.$subscribe(async (_, state) => {
    company.value = state.company
    const approversResponse = await getApproversByCompanyId(
      company.value?.id || ''
    )
    approvers.value = approversResponse
  })

  //variables used in the first step
  const selectedEntity = ref(entities.value[0])

  const approvers = ref([
    {
      id: '',
      name: 'Supervisor del empleado',
    },
  ])

  const notificationStore = useNotifications()

  const employees = ref()
  const payrolls = ref()
  const dialogVisible = ref(false)

  //variables used in the second step
  const approvalSteps = reactive({
    data: [] as ApprovalStep[],
    totalItems: 0,
  })

  watch(isEditMode, () => {
    const lastStepHasName =
      approvalSteps.data[approvalSteps.data.length - 1].approver?.name !== ''
    if (isEditMode && lastStepHasName) {
      approvalSteps.data.push({
        id: '1', //id used to edit the row in the table
        useEmployeeSupervisor: false,
        weight: 0,
        description: '',
        approver: {
          id: '',
          name: '',
          userId: '',
        },
        isBackup: false,
      })
    } else {
      approvalSteps.data.pop()
    }
  })

  const descriptionSchema = yup
    .string()
    .required('La descripción del flujo es requerida')
  const employeesSchema = yup
    .array()
    .min(1, 'Al menos un empleado debe ser seleccionado')
    .required('los empleados son requeridos')
  const payrollsSchema = yup
    .array()
    .min(1, 'Al menos una nómina debe ser seleccionado')
    .required('Las nóminas son requeridas')
  const {
    value: description,
    errorMessage: descriptionError,
    validate: descValidate,
  } = useField<string>('description', descriptionSchema)
  const {
    value: selectedEmployees,
    errorMessage: employeesError,
    validate: employeesValidate,
  } = useField<string[]>('selectedEmployees', employeesSchema)
  const {
    value: selectedPayrolls,
    errorMessage: payrollsError,
    validate: payrollsValidate,
  } = useField<string[]>('selectedPayrolls', payrollsSchema)
  const editingRows = ref([])

  onMounted(async () => {
    isLoading.value = true
    payrollConfigId.value = path.params.id
    approvalFlowId.value = path.params.flowId

    const approvalFlowResponse = await getApprovalFlowById(approvalFlowId.value)
    description.value = approvalFlowResponse.description

    if (approvalFlowResponse.onlyEmployees.length > 0) {
      selectedEntity.value = entities.value[1]
    }

    if (approvalFlowResponse.onlyPayrolls.length > 0) {
      selectedEntity.value = entities.value[2]
    }

    selectedPayrolls.value = approvalFlowResponse.onlyPayrolls
    selectedEmployees.value = approvalFlowResponse.onlyEmployees

    const employeesResponse = await getEmplopeesByPayrollConfigId(
      payrollConfigId.value
    )
    employees.value = employeesResponse.map((employee) => ({
      id: employee.id,
      name: employee.code + '-' + employee.name,
    }))

    const payrollReponse = await getPayrollsByConfigId(
      String(payrollConfigId.value)
    )
    payrolls.value = payrollReponse.map((payroll) => ({
      id: payroll.id,
      name: payroll.code + '-' + payroll.name,
    }))

    const approversResponse = await getApproversByCompanyId(
      company.value?.id || ''
    )
    approvers.value = approvers.value.concat(approversResponse)

    const approvalStepsResponse = await getApprovalFlowSteps(
      approvalFlowId.value
    )
    approvalSteps.data = approvalStepsResponse

    for (let step of approvalSteps.data) {
      removeApproverAssigned(step.approver?.id || '')
    }

    approvalSteps.totalItems = approvalStepsResponse.length
    isLoading.value = false
  })

  const updateDialogVisibility = (value: boolean) => {
    dialogVisible.value = value
  }

  const onRowDelete = (
    event: any,
    id: string,
    approverId: string,
    approverName: string
  ) => {
    if (approverId && approverName) {
      addApproverFromDeletedStep(approverId, approverName)
    } else {
      addApproverFromDeletedStep('', 'Supervisor del empleado')
    }

    if (isNaN(Number(id))) {
      stepsToDelete.stepsIds.push(id)
    }
    approvalSteps.data = approvalSteps.data.filter(
      (approvalStep) => approvalStep.id !== id
    )
  }

  const onSave = async () => {
    saving.value = true
    const isValid =
      (await descValidate()) &&
      (await employeesValidate()) &&
      (await payrollsValidate())

    if (approvalSteps.data.length === 1) {
      notificationStore.addErrorNotification({
        detail: 'Debe agregar al menos un paso de aprobación',
        severity: 'error',
        summary: 'Error',
      })
      saving.value = false
      return
    }

    await updateApprovalFlow(approvalFlowId.value, {
      description: description.value,
      payrollConfigId: payrollConfigId.value,
      onlyEmployees:
        selectedEntity.value.code === 'E' ? selectedEmployees.value : [],
      onlyPayrolls:
        selectedEntity.value.code === 'N' ? selectedPayrolls.value : [],
    })

    //Delete last step because is empty
    approvalSteps.data.pop()

    approvalSteps.data.forEach((step) => {
      if (
        step.approver?.name === 'Supervisor del empleado' ||
        step.useEmployeeSupervisor
      ) {
        step.useEmployeeSupervisor = true
      } else {
        step.useEmployeeSupervisor = false
      }

      if (isNaN(step.id as any)) {
        // If the step.id is not a number, it means that the step would be updated
        stepsToUpdate.flowSteps.push({
          id: step.id || '',
          approvalFlowId: step.approvalFlowId || '',
          approverId: step.approver?.id || '',
          description: step.description || '',
          isBackup: step.isBackup || false,
          useEmployeeSupervisor: step.useEmployeeSupervisor || false,
          weight: step.weight || 0,
        })
      } else if (!isNaN(step.id as any)) {
        //if the step is a number, it means that the step would be created
        stepsToCreate.steps.push({
          approvalFlowId: approvalFlowId.value,
          approverId: step.approver?.id || '',
          description: step.description || '',
          isBackup: step.isBackup || false,
          useEmployeeSupervisor: step.useEmployeeSupervisor || false,
          weight: step.weight || 0,
        })
      }
    })

    let stepsUdpdatedResponse
    let stepsCreatedResponse
    let stepsToDeleteResponse

    if (stepsToUpdate.flowSteps.length > 0) {
      stepsUdpdatedResponse = await updateApprovalFlowSteps(stepsToUpdate)
    }

    if (stepsToCreate.steps.length > 0) {
      stepsCreatedResponse = await createApprovalFlowSteps(stepsToCreate)
    }

    if (stepsToDelete.stepsIds.length > 0) {
      stepsToDeleteResponse = await deleteApprovalFlowSteps(stepsToDelete)
    }

    saving.value = false

    if (
      stepsUdpdatedResponse === 'OK' ||
      stepsCreatedResponse === 'OK' ||
      stepsToDeleteResponse === 'OK'
    ) {
      notificationStore.addSuccesNotification({
        detail: `200`,
        severity: 'success',
        summary: `Flujo de aprobación actualizado con exito`,
      })

      router.push('/payrollsConfig/' + payrollConfigId.value + '/approvalFlows')
    }

    saving.value = false
  }

  const onRowEditSave = (event: any) => {
    let { newData, data, index } = event
    approvalSteps.data[index] = newData

    //this is used when one approver is changed to another, so the previous approver is added to the list of approvers
    if (data.approver) {
      addApproverFromDeletedStep(data.approver.id, data.approver.name)
    } else {
      addApproverFromDeletedStep('', 'Supervisor del empleado')
    }

    //this is used when the approver is removed from the step
    removeApproverAssigned(newData.approver.id)

    //Add new row if the last row is not empty
    if (
      newData.approver?.name !== '' &&
      index === approvalSteps.data.length - 1
    ) {
      const newStepNumber = Number(approvalSteps.data[index].id) + 1
      approvalSteps.data.push({
        id: String(newStepNumber),
        useEmployeeSupervisor: false,
        weight: 0,
        description: '',
        approver: {
          id: '',
          name: '',
          userId: '',
        },
        isBackup: false,
      })
    }
  }

  const removeApproverAssigned = (id: string) => {
    approvers.value = approvers.value.filter((approver) => approver.id !== id)
  }

  const addApproverFromDeletedStep = (id: string, name: string) => {
    approvers.value.push({ id, name })
  }

  const isValidToSave = computed(() => {
    return (
      approvalSteps.data.length > 1 &&
      description.value &&
      (selectedEntity.value.code === 'ALL' ||
        (selectedEntity.value.code === 'E'
          ? selectedEmployees.value.length > 0
          : selectedPayrolls.value.length > 0))
    )
  })

  const calcApproverPlaceHolder = (step: ApprovalStep) => {
    if (step.useEmployeeSupervisor) {
      return 'Supervisor del empleado'
    } else if (step.approver?.name) {
      return step.approver?.name
    } else {
      return 'Sin asignar'
    }
  }

  const toggleEditMode = (value: boolean) => {
    isEditMode.value = value
  }
</script>
